import type { RunState } from '~/types/game'

/**
 * 📊 Lifetime player stats (v5.64, per Javi): a PRIVATE local ledger —
 * runs started/won/lost/abandoned per mode, mission outcomes, total
 * playtime. Shown in the title's Player Stats panel; never ranked
 * (the public face of mastery is the streak design — win RATES in
 * public punish playing, so the rate lives here, for your eyes).
 *
 * Local-only for now; the future server streak ledger can supersede.
 * Counting starts the day this shipped — an honest zero, like the
 * boards. The storage key is classified in PROGRESS_KEYS (a wipe
 * takes it) and this file sits in run-sync.test's key scan.
 */

const KEY = 'elemental-dispatch-lifetime-v1'

export interface ModeTally { started: number; won: number; lost: number; abandoned: number }
/** 🔥 v5.80 (per Javi): per-ascension CAMPAIGN win streaks - current
 * and highest. A win at A extends A's streak only; a loss or abandon
 * at A breaks only A's streak. Other ascensions never notice. */
export interface StreakTally { current: number; best: number }
export interface LifetimeStats {
  runs: { campaign: ModeTally; weekly: ModeTally; gauntlet: ModeTally }
  /** keyed by ascension number (campaign runs only) */
  streaks: Record<number, StreakTally>
  missions: { succeeded: number; failed: number; expired: number }
  playtimeMs: number
  /** 🏆 v6.45: hook-style marks the achievements read - a best, a
   * fastest, a flag - each ONE line at the event that sets it:
   * gauntletBestAct, fastestWinMs, legacyWin; v6.52 adds noQuarterWin,
   * pacifistWin, untouchedAct, brinkBoss, weeklyPodium. */
  marks: Record<string, number>
  /** 📜 v6.52: every story event this player has ever been dealt. */
  eventsSeen: string[]
}
export type StatsMode = keyof LifetimeStats['runs']
export type RunOutcome = 'won' | 'lost' | 'abandoned'

const zeroTally = (): ModeTally => ({ started: 0, won: 0, lost: 0, abandoned: 0 })
const zeroStats = (): LifetimeStats => ({
  runs: { campaign: zeroTally(), weekly: zeroTally(), gauntlet: zeroTally() },
  streaks: {},
  missions: { succeeded: 0, failed: 0, expired: 0 },
  playtimeMs: 0,
  marks: {},
  eventsSeen: [],
})

/** Pure so the roll tests can pin it: one settled campaign run at
 * `ascension` updates that ascension's streak in place. */
export function applyStreak(
  streaks: Record<number, StreakTally>, ascension: number, outcome: RunOutcome,
): void {
  let t = streaks[ascension]
  if (!t) {
    t = { current: 0, best: 0 }
    streaks[ascension] = t
  }
  if (outcome === 'won') {
    t.current++
    t.best = Math.max(t.best, t.current)
  } else {
    t.current = 0
  }
}

/** Which ledger lane a run belongs to. */
export function statsModeOf(run: Pick<RunState, 'mode' | 'gauntlet'>): StatsMode {
  if (run.mode === 'daily') return 'weekly'
  return run.gauntlet ? 'gauntlet' : 'campaign'
}

function normalize(raw: unknown): LifetimeStats {
  const base = zeroStats()
  if (!raw || typeof raw !== 'object') return base
  const r = raw as Partial<LifetimeStats>
  for (const mode of ['campaign', 'weekly', 'gauntlet'] as const) {
    for (const k of ['started', 'won', 'lost', 'abandoned'] as const) {
      const v = r.runs?.[mode]?.[k]
      if (typeof v === 'number' && Number.isFinite(v) && v >= 0) base.runs[mode][k] = Math.floor(v)
    }
  }
  const st = r.streaks
  if (st && typeof st === 'object') {
    for (const [k, v] of Object.entries(st)) {
      const asc = Number(k)
      const cur = (v as Partial<StreakTally>)?.current
      const best = (v as Partial<StreakTally>)?.best
      if (Number.isInteger(asc) && asc >= 0 && typeof cur === 'number' && typeof best === 'number'
        && Number.isFinite(cur) && Number.isFinite(best) && cur >= 0 && best >= 0) {
        base.streaks[asc] = { current: Math.floor(cur), best: Math.max(Math.floor(best), Math.floor(cur)) }
      }
    }
  }
  for (const k of ['succeeded', 'failed', 'expired'] as const) {
    const v = r.missions?.[k]
    if (typeof v === 'number' && Number.isFinite(v) && v >= 0) base.missions[k] = Math.floor(v)
  }
  if (typeof r.playtimeMs === 'number' && Number.isFinite(r.playtimeMs) && r.playtimeMs >= 0) {
    base.playtimeMs = Math.floor(r.playtimeMs)
  }
  if (r.marks && typeof r.marks === 'object') {
    for (const [k, v] of Object.entries(r.marks).slice(0, 50)) {
      if (typeof v === 'number' && Number.isFinite(v) && v >= 0) base.marks[k] = v
    }
  }
  if (Array.isArray(r.eventsSeen)) {
    base.eventsSeen = [...new Set(r.eventsSeen.filter((id): id is string => typeof id === 'string'))].slice(0, 100)
  }
  return base
}

export function useLifetimeStats() {
  const stats = useState<LifetimeStats>('lifetime-stats', zeroStats)
  const loaded = useState<boolean>('lifetime-stats-loaded', () => false)

  function load() {
    if (loaded.value || !import.meta.client) return
    loaded.value = true
    try {
      const raw = localStorage.getItem(KEY)
      if (raw) stats.value = normalize(JSON.parse(raw))
    } catch { /* a broken ledger starts over — it is only ever additive */ }
  }
  function save() {
    if (!import.meta.client) return
    try { localStorage.setItem(KEY, JSON.stringify(stats.value)) } catch { /* full/blocked storage: skip */ }
  }

  function recordRunStart(mode: StatsMode) {
    load()
    stats.value.runs[mode].started++
    save()
  }
  /** 🏆 v6.45: what the run-end site knows that the achievements want. */
  interface RunEndExtra {
    /** The act the run reached (the Gauntlet's depth). */
    act?: number
    /** Legacy was on the roster at the end. */
    legacyOnRoster?: boolean
    /** v6.52: not one mission expired all run. */
    noExpiry?: boolean
    /** v6.52: no elite or titan node was ever fought. */
    noElite?: boolean
    /** 🎗️🧘 v6.54: the homage chains - all four need Ascension 10 cleared
     * at the moment of an A8+ win. */
    a10Cleared?: boolean
    keepsakeOnly?: boolean
    lessonOnly?: boolean
    keepsakeAndLesson?: boolean
    ascetic?: boolean
  }
  function recordRunEnd(mode: StatsMode, outcome: RunOutcome, playtimeMs: number, ascension?: number, extra: RunEndExtra = {}) {
    load()
    stats.value.runs[mode][outcome]++
    // 🏆 v6.45: the marks. Deepest Gauntlet act whatever the ending;
    // fastest campaign win; Legacy carried to a win.
    const marks = stats.value.marks
    if (mode === 'gauntlet' && typeof extra.act === 'number' && Number.isFinite(extra.act)) {
      marks.gauntletBestAct = Math.max(marks.gauntletBestAct ?? 0, Math.floor(extra.act))
    }
    if (mode === 'campaign' && outcome === 'won') {
      if (Number.isFinite(playtimeMs) && playtimeMs > 0) {
        marks.fastestWinMs = Math.min(marks.fastestWinMs ?? Number.POSITIVE_INFINITY, Math.floor(playtimeMs))
        // 💨 v6.57 (per Javi): Like the Wind wants Ascension 8 or above - its own mark.
        if (typeof ascension === 'number' && ascension >= 8) marks.fastestWinA8Ms = Math.min(marks.fastestWinA8Ms ?? Number.POSITIVE_INFINITY, Math.floor(playtimeMs))
      }
      if (extra.legacyOnRoster) marks.legacyWin = 1
    }
    // 🏆 v6.52: a clean sheet and the long way round count for any
    // expedition with a summit - campaign or weekly, never the Gauntlet.
    // v6.53 (per Javi): with a floor - Ascension 8 for the clean sheet,
    // 6 for the long way round - so neither is a Normal-run freebie.
    if (mode !== 'gauntlet' && outcome === 'won' && typeof ascension === 'number') {
      if (extra.noExpiry && ascension >= 8) marks.noQuarterWin = 1
      if (extra.noElite && ascension >= 6) marks.pacifistWin = 1
      // 🎗️🧘 v6.54: the homage chains. Ascension 8+, and Ascension 10
      // already cleared when the boss fell.
      if (ascension >= 8 && extra.a10Cleared) {
        if (extra.keepsakeOnly) marks.emptyHanded = 1
        if (extra.lessonOnly) marks.singleLesson = 1
        if (extra.keepsakeAndLesson) marks.keptBoth = 1
        if (extra.ascetic) marks.ascetic = 1
      }
    }
    // 🔥 Streaks are a CAMPAIGN ladder (weekly is a shared seed, the
    // gauntlet its own beast) - and only when the caller knows the
    // run's ascension.
    if (mode === 'campaign' && typeof ascension === 'number' && Number.isInteger(ascension) && ascension >= 0) {
      applyStreak(stats.value.streaks, ascension, outcome)
    }
    if (Number.isFinite(playtimeMs) && playtimeMs > 0) stats.value.playtimeMs += Math.floor(playtimeMs)
    save()
  }
  function recordMission(kind: keyof LifetimeStats['missions']) {
    load()
    stats.value.missions[kind]++
    save()
  }
  /** 🏆 v6.52: set a mark at the event that earns it (keeps the higher value). */
  function mark(key: string, value: number) {
    load()
    const marks = stats.value.marks
    if ((marks[key] ?? 0) >= value) return
    marks[key] = value
    save()
  }
  /** 📜 v6.52: a story event dealt to this player, once ever. */
  function recordEvent(id: string) {
    load()
    if (stats.value.eventsSeen.includes(id)) return
    stats.value.eventsSeen = [...stats.value.eventsSeen, id]
    save()
  }

  return { stats, load, recordRunStart, recordRunEnd, recordMission, mark, recordEvent }
}
